import {
  Combobox,
  EmptyState,
  Text,
  SearchIcon,
  ChannelAvatar,
  VisuallyHidden,
  AllChannelsIcon,
  Button,
  PlusIcon,
  type ChannelType,
} from '@buffer-mono/popcorn'

import type { FilterByChannel_ChannelFragment } from '~publish/gql/graphql'
import { type FragmentType, graphql, getFragmentData } from '~publish/gql'

import { FilterButton } from '../FilterButton'

import styles from './FilterByChannel.module.css'
import { getAccountChannelsURL } from '~publish/legacy/utils/formatters/getURL'
import { useIsAdmin } from '~publish/contexts/AccountContext'
import { OpenChannelStoreFrontTrigger } from '../OpenChannelStoreFrontTrigger'

export const FilterByChannel_Channel = graphql(/* GraphQL */ `
  fragment FilterByChannel_Channel on Channel {
    id
    type
    name
    service
    avatar
    serverUrl
    timezone
    locationData {
      location
    }
  }
`)

/**
 * Masked type provided by parent query result.
 * Use `useFilterByChannelFragment` to get the unmasked type.
 */
export type MaskedFilterByChannelFragment = readonly FragmentType<
  typeof FilterByChannel_Channel
>[]

/**
 * Unmasked type extracted from parent query result.
 */
export type FilterByChannelFragment = FilterByChannel_ChannelFragment

/**
 * List of unmasked channel filter fragments extracted from parent query result.
 */
export type FilterByChannelFragments =
  readonly FilterByChannel_ChannelFragment[]

/**
 * Unmask channel filters from the parent query result into typed
 *  fragments.
 * @param channels masked channel filter fragments from the parent query result
 * @returns
 */
export const useFilterByChannelFragment = (
  channels: MaskedFilterByChannelFragment,
): FilterByChannelFragments =>
  getFragmentData(FilterByChannel_Channel, channels)

type FilterByChannelProps = {
  channels: MaskedFilterByChannelFragment
  /**
   * Selected channel ids
   */
  value: string[]
  onSelect: (channelIds: string[]) => void
}

/**
 * Component to enable filtering by channels, using Combobox
 * It exposes fragment to fetch channels, tags array and selected tags
 */
export const FilterByChannel = ({
  channels: passedChannels,
  value,
  onSelect,
}: FilterByChannelProps): JSX.Element => {
  const channels = getFragmentData(FilterByChannel_Channel, passedChannels)
  const isAdmin = useIsAdmin()

  const emptyStateAdmin = (
    <EmptyState size="small" variant="primary">
      <EmptyState.Icon>
        <AllChannelsIcon />
      </EmptyState.Icon>
      <EmptyState.Heading>No channels found</EmptyState.Heading>
      <EmptyState.Description>
        {isAdmin && (
          <OpenChannelStoreFrontTrigger cta="publish-filterByChannel-connectAChannel-1">
            <Button>
              <PlusIcon /> Connect a Channel
            </Button>
          </OpenChannelStoreFrontTrigger>
        )}
      </EmptyState.Description>
    </EmptyState>
  )

  const emptyStateMember = (
    <EmptyState size="small" variant="primary">
      <EmptyState.Heading>Filter posts by channel</EmptyState.Heading>
      <EmptyState.Description>
        Contact an account administrator to access to your team&apos;s channels.
      </EmptyState.Description>
    </EmptyState>
  )

  return (
    <Combobox multiple value={value} onChange={onSelect}>
      <Combobox.Trigger>
        <FilterButton
          size="medium"
          icon={<AllChannelsIcon />}
          count={value.length}
          variant="tertiary"
        >
          Channels
        </FilterButton>
      </Combobox.Trigger>
      <Combobox.Content>
        {channels.length > 0 && (
          <Combobox.Input
            placeholder="Search channels"
            prefix={<SearchIcon />}
          />
        )}
        <Combobox.List>
          <Combobox.Empty>
            {channels.length === 0 && isAdmin && emptyStateAdmin}
            {channels.length === 0 && !isAdmin && emptyStateMember}
            {channels.length > 0 && (
              <EmptyState size="small">
                <EmptyState.Icon>
                  <SearchIcon />
                </EmptyState.Icon>
                <EmptyState.Description>
                  No channels found
                </EmptyState.Description>
              </EmptyState>
            )}
          </Combobox.Empty>
          {channels.map(
            ({ id, name = '', service, avatar, locationData, serverUrl }) => (
              <Combobox.Item
                key={id}
                value={id}
                keywords={[
                  name,
                  service,
                  ...(locationData?.location ? [locationData?.location] : []),
                ]}
              >
                <div className={styles.channel}>
                  <ChannelAvatar
                    size="small"
                    alt=""
                    src={avatar}
                    name={name}
                    channel={service as ChannelType}
                    className={styles.avatar}
                  />
                  <Text weight="medium" className={styles.name}>
                    {name}
                    <VisuallyHidden>({service})</VisuallyHidden>
                  </Text>
                  {locationData?.location && (
                    <Text color="subtle" className={styles.subtitle}>
                      {locationData?.location}
                    </Text>
                  )}
                  {serverUrl && (
                    <Text color="subtle" className={styles.subtitle}>
                      {serverUrl}
                    </Text>
                  )}
                </div>
              </Combobox.Item>
            ),
          )}
        </Combobox.List>

        {channels.length === 1 && (
          <Combobox.Footer>
            <Button
              as="a"
              href={getAccountChannelsURL()}
              variant="tertiary"
              size="large"
            >
              <PlusIcon /> Connect More Channels
            </Button>
          </Combobox.Footer>
        )}
      </Combobox.Content>
    </Combobox>
  )
}
